import { useTranslation } from "react-i18next";
import { Badge, Table } from "@/shared/ui";
import { CheckIcon, CloseIcon } from "@/shared/assets/icons";
import { formatDateForDisplay, type SortConfig } from "@/shared/utils";
import type { Volunteer } from "../types";
import type { VolunteerSortKey } from "../hooks";

interface Props {
  volunteers: Volunteer[];
  locale: string;
  sortConfig: SortConfig<VolunteerSortKey> | null;
  onSort: (key: VolunteerSortKey) => void;
  onSelectVolunteer: (volunteer: Volunteer) => void;
}

function getRegionName(volunteer: Volunteer, locale: string): string {
  if (!volunteer.region) return "";
  const key = `name_${locale}` as keyof typeof volunteer.region;
  const value = volunteer.region[key];
  if (typeof value === "string" && value) return value;
  return volunteer.region.name_ru || "";
}

export default function VolunteersTableView({ volunteers, locale, sortConfig, onSort, onSelectVolunteer }: Props) {
  const { t } = useTranslation("VolunteersPage");

  return (
    <div className="w-full min-w-0">
      <Table.Table scrollable tableClassName="min-w-[900px]">
        <Table.Header>
          <tr>
            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "full_name" ? sortConfig.direction : undefined}
              onSort={() => onSort("full_name")}
            >
              {t("table.fullName")}
            </Table.HeadCell>
            <Table.HeadCell>{t("table.email")}</Table.HeadCell>
            <Table.HeadCell>{t("table.phone")}</Table.HeadCell>
            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "region" ? sortConfig.direction : undefined}
              onSort={() => onSort("region")}
            >
              {t("table.region")}
            </Table.HeadCell>
            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "projects_count" ? sortConfig.direction : undefined}
              onSort={() => onSort("projects_count")}
            >
              {t("table.projectsCount")}
            </Table.HeadCell>
            <Table.HeadCell>{t("table.dateOfBirth")}</Table.HeadCell>
            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "is_active" ? sortConfig.direction : undefined}
              onSort={() => onSort("is_active")}
            >
              {t("table.status")}
            </Table.HeadCell>
          </tr>
        </Table.Header>

        <Table.Body>
          {volunteers.map(function renderVolunteerRow(volunteer) {
            return (
              <Table.Row
                key={volunteer.id}
                onClick={() => onSelectVolunteer(volunteer)}
              >
                <Table.Cell>{volunteer.full_name}</Table.Cell>
                <Table.Cell>{volunteer.email}</Table.Cell>
                <Table.Cell>{volunteer.phone}</Table.Cell>
                <Table.Cell>{getRegionName(volunteer, locale)}</Table.Cell>
                <Table.Cell>{volunteer.projects_count}</Table.Cell>
                <Table.Cell>{formatDateForDisplay(volunteer.date_of_birth)}</Table.Cell>
                <Table.Cell>
                  <Badge
                    variant="soft"
                    color={volunteer.is_active ? "positive" : "negative"}
                    text={volunteer.is_active ? t("table.active") : t("table.inactive")}
                    icon={volunteer.is_active ? <CheckIcon /> : <CloseIcon size={16} />}
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Table>
    </div>
  );
}
